<template>
  <v-dialog
    v-model="dialog"
    max-width="1600px"
  >
    <template v-slot:activator="{ on: dialog }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn 
            icon
            v-on="{ ...dialog, ...tooltip }"
          >
            <v-icon medium>
              mdi-calendar-search
            </v-icon>
          </v-btn>
        </template>
        <span>Department Leave Dashboard</span>
      </v-tooltip>
    </template>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card color="#6bb0d9">
          <v-card-title
            class="justify-center pa-3"
          >
            <h3 class="title white--text ma-auto">
              Department Leave Dashboard
            </h3>
          </v-card-title> 
          <v-card-actions
            class="justify-center mx-0 pt-1 pb-2"
            color="#6bb0d9"
          >
            <v-sheet
              class="mx-auto"
              color="#6bb0d9"
              max-width="600"
            >
              <v-slide-group
                class="px-2"
                show-arrows
                center-active
                :value="currentMonthIndex"
                mandatory
                dark
              >
                <v-slide-item
                  v-for="(month, i) in months"
                  :key="i"
                  v-slot:default="{ active, toggle }"
                >            
                  <v-btn
                    class="mx-2"
                    :input-value="active"
                    active-class="white--text"
                    text
                    @click="toggle(); onMonthChanged(month);"
                  >
                    {{ `${month.name} ${month.year}` }}
                  </v-btn>
                </v-slide-item>
              </v-slide-group>
            </v-sheet>
          </v-card-actions>
          <v-data-table
            :headers="dateHeaders"
            :loading="loading"
            :items="filteredSubordinates"
            :search="search"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            multi-sort
            item-key="employee_uid"
            :height="filteredSubordinates.length > 10 ? '600px' : ''"
            hide-default-footer
            fixed-header
            disable-pagination
            hide-default-header
          >
            <template v-slot:header="{ props: { headers } }">
              <thead class="v-data-table-header">
                <tr>
                  <th class="pa-1">
                    <v-text-field
                      v-model="search"
                      class="custom"
                      prepend-icon="mdi-magnify"
                      label="Search employees"
                      dense
                      single-line
                      hide-details
                    />
                  </th>
                  <v-tooltip
                    v-for="(header, i) in headers.slice(1, headers.length)"
                    :key="i"
                    max-width="250px"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <th       
                        :width="header.width"       
                        :class="[
                          {'sortable': header.sortable },
                          {'active': header.text == sortBy[0] },
                          'text-center',
                          isToday(header.text) ? 'light-blue lighten-4' : {'grey lighten-5': isDayExcluded(header.text)},
                        ]"
                        v-on="on"
                        @click="sort(header.value)"
                      >
                        <span
                          :class="[
                            {'red--text text--lighten-2': isHoliday(header.text)}, 
                            header.text == sortBy[0] ? 'body-2' : 'caption', 
                            'font-weight-medium'
                          ]"
                        >
                          {{ header.text }}
                        </span>
                      </th>
                    </template>
                    <v-card-subtitle class="py-1">
                      {{ getDayOfDate(header.text) }}
                    </v-card-subtitle>
                    <div v-if="isHoliday(header.text)">
                      <v-divider />
                      <v-card-subtitle class="font-weight-bold py-1">
                        {{ (isHoliday(header.text) || {}).name || '' }}
                      </v-card-subtitle>
                    </div>
                  </v-tooltip>
                </tr>
              </thead>
            </template>
            <template v-slot:item="{ item, headers }"> 
              <tr>
                <td>{{ item.person.name }}</td>
                <td
                  v-for="(header, i) in headers.slice(1, headers.length)"
                  :key="i"
                  :class="[
                    isToday(header.text) ? 'light-blue lighten-4' : {'grey lighten-5': isDayExcluded(header.text)}, 
                    'body-td', 'px-0'
                  ]"
                >
                  <v-tooltip
                    v-if="currentLeaveApplication(item.leave_applications, header.text)"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <div
                        :class="[                          
                          {
                            'secondary': currentLeaveApplication(item.leave_applications, header.text).approval_request.status
                          },
                          {
                            'div-on-leave': getCurrentLeaveApplicationSession(item.leave_applications, header.text).leave_session_id === 4
                          },
                          {
                            'div-on-leave-not-full-day': getCurrentLeaveApplicationSession(item.leave_applications, header.text).leave_session_id === 2 || getCurrentLeaveApplicationSession(item.leave_applications, header.text).leave_session_id === 3
                          },
                          {
                            'div-on-leave-pending-approved': getCurrentLeaveApplicationSession(item.leave_applications, header.text).color_id === 3
                          },
                          {
                            'div-on-leave-approved-pending': getCurrentLeaveApplicationSession(item.leave_applications, header.text).color_id === 4
                          },
                          'text-center'
                        ]"
                        v-on="on"
                      >
                        <span>
                          <v-icon
                            v-if="getCurrentLeaveApplicationSession(item.leave_applications, header.text).leave_session_id === 2"
                            small
                            color="yellow"
                          >
                            fa fa-sun-o
                          </v-icon>
                          <v-icon
                            v-if="getCurrentLeaveApplicationSession(item.leave_applications, header.text).leave_session_id === 3"
                            small
                            color="yellow"
                          >
                            fa fa-moon-o
                          </v-icon>
                      
                        </span>
                      </div>
                    </template>
                    <span>{{ currentLeaveApplication(item.leave_applications, header.text).leave_entitlement.leave_type.name }}</span>
                  </v-tooltip>            
                </td>
              </tr>
            </template>
            <template v-slot:body.append="{ headers }">
              <tr>
                <td
                  class="footer-td"
                  :colspan="headers.length"
                >
                  <table>
                    <tr>
                      <td class="text-right">
                        <div
                          style="background-color:#4caf50"
                          class="box"
                        />
                      </td>
                      <td class="text-left">
                        Approved Leave
                      </td>
                      <td class="text-right" />
                      <td class="text-left" />
                      <td class="text-right">
                        <div
                          style="background-color:#ffa21a"
                          class="box"
                        />
                      </td>
                      <td class="text-left">
                        Pending Leave
                      </td>
                      <td class="text-right" />
                      <td class="text-left" />
                      <td class="text-right">
                        <div
                          class="box div-on-leave-approved-pending"
                        />
                      </td>
                      <td class="text-left">
                        AM Approved, PM Pending
                      </td>
                      <td class="text-right" />
                      <td class="text-left" />
                      <td class="text-right">
                        <div
                          class="box div-on-leave-pending-approved"
                        />
                      </td>
                      <td class="text-left">
                        AM Pending, PM Approved
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-dialog>
</template>

<script>
  export default {
    props: {
      currentEmployeeUid: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        dialog: null,
        search: '',
        loading: false,
        sortBy: [this.$moment().get('date').toString(), 'person.name'],
        sortDesc: [false, false],
        subordinates: [],
        holidayDetails: [],
        currentMonthIndex: this.$moment().month(),
        selectedMonth: this.$moment().month() + 1,
        selectedYear: this.$moment().year(),
        headers: [
          {
            text: '',
            sortable: false,
            filterable: true,
            value: 'person.name',
            width: '10%',
          },
        ],
        filters: {
          branch: '',
          department: '',
        },
        currentEmployee: null,
      }
    },
    
    computed: {
      dateHeaders () {
        let headersCopy = [...this.headers]
        const daysInMonth = this.$moment(this.selectedYearMonth, 'YYYY-MM').daysInMonth()
        const headerWidth = (90 / daysInMonth)

        for (let i = 0; i < daysInMonth; i++) {
          const day = i + 1

          headersCopy.push({
            text: day,
            sortable: true,
            filterable: false,
            value: `${day}`,
            width: `${headerWidth}%`,
          })
        } 
      
        return headersCopy
      },
      filteredSubordinates () {
        return this.subordinates
          .filter(this.filter)
      },
      isFiltering () {
        return !_.isEmpty(this.filters.branch) ||
          !_.isEmpty(this.filters.department)
      },
      holidayDates () {
        return this.holidayDetails.map(holidayDetail => {
          return { name: holidayDetail.holiday.name, date: holidayDetail.date }
        })
      },
      selectedYearMonth () {
        return `${this.selectedYear}-${this.selectedMonth}`
      },
      months () {
        return this.getMonthsOfYear(this.currentYear).concat(this.getMonthsOfYear(this.nextYear))
      },  
      currentYear () {
        return this.$moment().year()
      },
      nextYear () {
        return this.$moment().add(1, 'year').year()
      },
    },
    watch: {
      dialog (value) {
        this.loadSubordinates()
        this.loadHolidays()
      },
    },
    methods: {
      isDayExcluded (day) {
        const date = `${this.selectedYearMonth}-${day}`

        return this.$moment(date, 'YYYY-MM-DD').day() === 0 || this.isHoliday(day)
      },
      loadSubordinates () {
        this.loading = true
        
        Promise.all([
          this.$api.departmentWithLeaves().list(this.prepareFormData()),
          this.$api.employees().show(this.currentEmployeeUid),
        ]).then(([departmentWithLeaves, employee]) => {
          this.subordinates = departmentWithLeaves.data
          this.currentEmployee = employee.data

          this.loading = false
        })
      },
      loadHolidays () {
        let yearMonth = this.$moment(this.selectedYearMonth, 'YYYY-MM').format('YYYY-MM-DD')
        this.$api.hrisHolidayDetails().list('?filter[year]=' + this.$moment(yearMonth).year())
          .then(response => {
            let holidayDetailsData = response.data
            this.holidayDetails = holidayDetailsData.filter(holidayDetail => {
              if (holidayDetail.holiday.is_national) return holidayDetail
          
              if (holidayDetail.holiday_states.length > 0) {
                return holidayDetail.holiday_states.some(state => state.state_id === this.currentEmployee.city.state_id && state.department_id === this.currentEmployee.department_id) 
              }
              return false
            })
          })
      },      
      sort (val) {
        this.$set(this.sortBy, 0, val)
        this.$set(this.sortDesc, 0, !this.sortDesc[0])
      },
      filter (subordinate) {
        if (!this.isFiltering) return subordinate
      },
      currentLeaveApplication (leaveApplications, day) {
        let filteredLeave = leaveApplications.filter(leaveApplication => {
          return leaveApplication.leave_days.some(leaveDay => {
            return this.$moment(`${this.selectedYearMonth}-${day}`).isSame(leaveDay.date)
          })
        })

        if (filteredLeave.length > 1) {
          let approvalStatus = filteredLeave[0].approval_request.status ? filteredLeave[0].approval_request.status.name : null
          
          let filteredDay = filteredLeave[0].leave_days.find(leaveDay => {
            return this.$moment(`${this.selectedYearMonth}-${day}`).isSame(leaveDay.date)
          })
          
          let leaveType = filteredLeave[0].leave_entitlement.leave_type.name
          const leaveTypeWithSession = leaveType + ' (' + filteredDay.leave_session.name + ')'

          filteredLeave.splice(0, 1) // remove index 0 
          filteredLeave.forEach(leave => {
            if (leave.approval_request.status && leave.approval_request.status.name !== approvalStatus) {
              approvalStatus = approvalStatus + ', ' + leave.approval_request.status.name
            } else if (!leave.approval_request.status && !approvalStatus) {
              approvalStatus = null
            }

            if (leave.leave_entitlement.leave_type.name !== leaveType) {
              const filteredDayForOtherLeave = leave.leave_days.find(leaveDay => {
                return this.$moment(`${this.selectedYearMonth}-${day}`).isSame(leaveDay.date)
              })
              leaveType = leaveTypeWithSession + ', ' + leave.leave_entitlement.leave_type.name + ' (' + filteredDayForOtherLeave.leave_session.name + ')'
            } else {
              leaveType = leaveType + ' (A.M & P.M)'
            }
          })

          if (!approvalStatus) {
            return {
              start_date: this.$moment(`${this.selectedYearMonth}-${day}`).format('YYYY-MM-DD'),
              end_date: this.$moment(`${this.selectedYearMonth}-${day}`).format('YYYY-MM-DD'),
              requested_days: 1,
              approval_request: {
                status: null,
              },
              leave_entitlement: {
                leave_type: {
                  id: 99,
                  name: leaveType,
                },
              },
            }
          } else {
            return {
              start_date: this.$moment(`${this.selectedYearMonth}-${day}`).format('YYYY-MM-DD'),
              end_date: this.$moment(`${this.selectedYearMonth}-${day}`).format('YYYY-MM-DD'),
              requested_days: 1,
              approval_request: {
                status: {
                  id: 99,
                  name: approvalStatus,
                },
              },
              leave_entitlement: {
                leave_type: {
                  id: 99,
                  name: leaveType,
                },
              },
            }
          }
        } else {
          return leaveApplications.find(leaveApplication => {
            return leaveApplication.leave_days.some(leaveDay => {
              return this.$moment(`${this.selectedYearMonth}-${day}`).isSame(leaveDay.date)
            })
          })
        }      
      },      
      getCurrentLeaveApplicationSession (leaveApplications, day) {
        let obj = {}
        let sessions = []

        leaveApplications.map(leaveApplication => {
          leaveApplication.leave_days.map((leaveDay, index) => {
            if (this.$moment(`${this.selectedYearMonth}-${day}`).format('YYYY-MM-DD') === leaveDay.date) {
              leaveDay.status = leaveApplication.approval_request.status
              sessions.push(leaveDay)
            }
          })
        })

        if (sessions.length === 1) {
          sessions.forEach(session => {
            obj = session
            obj.color_id = session.status ? 2 : 1
          })
        } else { // am and pm 
          let colorId = ''

          sessions = _.orderBy(sessions, ['leave_session_id'], ['asc']) // am -> pm

          if (!sessions[0].status && !sessions[1].status) {
            colorId = 1
          } else if (sessions[0].status && sessions[1].status) {
            colorId = 2
          } else if (!sessions[0].status && sessions[1].status) {
            colorId = 3
          } else {
            colorId = 4
          }

          obj = {
            date: this.$moment(`${this.selectedYearMonth}-${day}`).format('YYYY-MM-DD'),
            leave_session_id: 4,
            color_id: colorId,
            leave_session: {
              id: 4,
              name: 'Full Day',
              value: 1,
            },
          }
        }
     
        return obj
      },
      getDayOfDate (day) {
        const date = `${this.selectedYearMonth}-${day}`

        return this.$moment(date, 'YYYY-MM-DD').format('ddd')
      },
      isHoliday (day) {
        const date = `${this.selectedYearMonth}-${day}`

        return this.holidayDates.find(holidayDate => holidayDate.date === this.$moment(date).format('YYYY-MM-DD'))
      },
      isToday (day) {
        const date = `${this.selectedYearMonth}-${day}`

        return this.$moment(date, 'YYYY-MM-DD').isSame(this.$moment().format('YYYY-MM-DD'))
      },
      getMonthsOfYear (year) {
        return this.$moment.monthsShort().map(month => {
          return {
            name: month,
            year: year,
          }
        })
      },
      onMonthChanged (month) {
        this.selectedMonth = this.$moment().month(month.name).month() + 1
        this.selectedYear = month.year
        this.loadHolidays()
        this.loadSubordinates()
      },
      prepareFormData () {
        return {
          selected_date: this.selectedYearMonth + '-01',
        }
      },
    },
  }
</script>

<style scoped>
  .approved-leave {
    background: green !important;
  }
  .v-data-table .v-data-table-header th.sortable.active {
    color: rgb(0, 0, 0);
  }

  .body-td {
    border-left: 1px solid rgba(0,0,0,.04);
  }

  .footer-td {
    border-top: 1px solid rgba(0,0,0,.04);
    border-left: 1px solid rgba(0,0,0,.04);
  }

  .v-data-table--fixed-header tr .footer-td{
    background: #FFFFFF;
    position: sticky;
    bottom:0;
    display: 'inline-block';
    white-space: nowrap;
  }

  .div-on-leave {
    background: #ffa21a;
    padding:10.5px 0px;
  }

  .div-on-leave-not-full-day {
    background: #ffa21a;
    padding:0px;
  }

  .div-on-leave-approved-pending {
    background-image: linear-gradient(to right, #4caf50, #ffa21a);
    padding:10.5px 0px;
  }

  .div-on-leave-pending-approved {
    background-image: linear-gradient(to right, #ffa21a, #4caf50);
    padding:10.5px 0px;
  }

  .box {
    width: 40px;
    height: 20px;
    margin: 0;
    padding: 10px 10px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  table {
    width: 500px;
    border: none !important;
  }
</style>
